'use client';

import { AgentOptions } from '@newrelic/browser-agent/loaders/agent';
import { useEffect } from 'react';
import { CONFIG } from 'src/config-global';

// Capture the original (unwrapped) console.error before Next.js patches it.
// Next.js wraps console.error with isNextRouterError filter during hydration,
// which triggers New Relic warning #64 ("wrapped function detected").
// By saving the native reference early (module-level), we can temporarily
// restore it during NR initialization so the agent wraps the real one.
const nativeConsoleError =
  typeof window !== 'undefined' ? window.console.error.bind(window.console) : undefined;

export function NewRelicBrowserProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;
    // Prevent double initialization - check both flag and if agent already exists
    if (window.newrelic || window.__NR_AGENT_INITIALIZED__) return;

    // Set flag before any async operations
    window.__NR_AGENT_INITIALIZED__ = true;

    const initNewRelic = async () => {
      const { accountId, agentId, applicationId, browserLicenseKey, trustKey } = CONFIG.newRelic;
      const { serverUrl } = CONFIG.site;

      if (!browserLicenseKey || !applicationId || !accountId || !trustKey || !agentId) {
        console.warn('New Relic Browser Agent: Missing credentials');
        return;
      }

      try {
        // Dynamically import BrowserAgent only on client side
        const { BrowserAgent } = await import('@newrelic/browser-agent/loaders/browser-agent');

        // Temporarily restore native console.error so NR wraps the
        // original instead of Next.js's wrapper (avoids warning #64).
        const nextWrappedError = window.console.error;
        if (nativeConsoleError) {
          window.console.error = nativeConsoleError;
        }

        const nrOptions: AgentOptions = {
          init: {
            distributed_tracing: {
              enabled: true,
              cors_use_newrelic_header: true,
              cors_use_tracecontext_headers: true,
              allowed_origins: [serverUrl], // API ORIGIN
            },
            privacy: { cookies_enabled: true },
            session_replay: {
              enabled: true,
              // Privacy model: mask all INPUT VALUES by default so no typed PII
              // (email, phone, address, passwords, etc.) can ever leak — this is
              // safe even for fields added in the future. Page TEXT and UI stay
              // readable so developers still get useful debugging context.
              mask_all_inputs: true,
              // Only mask text nodes explicitly marked private (e.g. PII rendered
              // as plain text in tables/headers). Default text stays visible.
              mask_text_selector: '[data-nr-mask], .nr-mask',
              // Fully block (replace with a placeholder) elements explicitly
              // marked as private — e.g. payment widgets, sensitive regions.
              block_selector: '[data-nr-block], .nr-block',
            },
            logging: { enabled: true, autoStart: true },
            ajax: {
              enabled: true,
              deny_list: ['bam.eu01.nr-data.net'],
            },
          },
          info: {
            beacon: 'bam.eu01.nr-data.net',
            errorBeacon: 'bam.eu01.nr-data.net',
            licenseKey: browserLicenseKey,
            applicationID: applicationId,
            sa: 1,
          },
          loader_config: {
            accountID: accountId,
            trustKey,
            agentID: agentId,
            licenseKey: browserLicenseKey,
            applicationID: applicationId,
          },
        };

        const agent = new BrowserAgent(nrOptions);
        if (CONFIG.site.version) {
          try {
            agent.addRelease(CONFIG.site.version, CONFIG.site.version);
            agent.setApplicationVersion(CONFIG.site.version);
          } catch (e) {
            // Non-fatal — agent may not support these methods in all versions.
            console.warn('NR release/version API not supported by agent version:', e);
          }
        }

        // Restore Next.js's console.error wrapper so router errors
        // continue to be filtered correctly.
        window.console.error = nextWrappedError;

        console.log('New Relic Browser Agent initialized', {
          version: CONFIG.site.version,
        });
      } catch (error) {
        console.error('Failed to initialize New Relic Browser Agent:', error);
      }
    };

    initNewRelic();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const err = reason instanceof Error ? reason : new Error(String(reason));
      window.newrelic?.noticeError(err, { source: 'unhandledrejection' });
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, []);

  return <>{children}</>;
}
